import { createSlice } from '@reduxjs/toolkit'
import type { RootState } from '.'

interface FluxLiveState {
  isLive: boolean
  lastUpdate: number | null
}

interface BurstLiveState {
  isLive: boolean
  lastUpdate: number | null
}

interface LiveState {
  flux: FluxLiveState
  burst: BurstLiveState
}

// Time in milliseconds to consider data as "live" (10 minutes)
const LIVE_TIMEOUT = 10 * 60 * 1000

// LocalStorage key
const LIVE_STATE_KEY = 'rosie_live_state'

// Load initial state from localStorage
const loadStateFromLocalStorage = (): LiveState => {
  try {
    const savedState = localStorage.getItem(LIVE_STATE_KEY)
    if (savedState) {
      const parsed = JSON.parse(savedState) as LiveState
      const now = Date.now()
      
      // Validate and restore state, checking if still within timeout
      return {
        flux: {
          isLive: parsed.flux.lastUpdate && (now - parsed.flux.lastUpdate) <= LIVE_TIMEOUT ? parsed.flux.isLive : false,
          lastUpdate: parsed.flux.lastUpdate,
        },
        burst: {
          isLive: parsed.burst.lastUpdate &&
           (now - parsed.burst.lastUpdate) <= LIVE_TIMEOUT ? parsed.burst.isLive : false,
          lastUpdate: parsed.burst.lastUpdate,
        },
      }
    }
  } catch (error) {
    console.error('Error loading live state from localStorage:', error)
  }
  
  return {
    flux: {
      isLive: false,
      lastUpdate: null,
    },
    burst: {
      isLive: false,
      lastUpdate: null,
    },
  }
}

// Save state to localStorage
const saveStateToLocalStorage = (state: LiveState) => {
  try {
    localStorage.setItem(LIVE_STATE_KEY, JSON.stringify(state))
  } catch (error) {
    console.error('Error saving live state to localStorage:', error)
  }
}

const initialState: LiveState = loadStateFromLocalStorage()

export const liveSlice = createSlice({
  name: 'live',
  initialState,
  reducers: {
    setFluxLive: state => {
      state.flux.isLive = true
      state.flux.lastUpdate = Date.now()
      saveStateToLocalStorage(state)
    },
    clearFluxLive: state => {
      state.flux.isLive = false
      state.flux.lastUpdate = null
      saveStateToLocalStorage(state)
    },
    setFluxNoData: state => {
      // When receiving NO_DATA, mark as not live but keep timestamp
      state.flux.isLive = false
      saveStateToLocalStorage(state)
    },
    setBurstLive: state => {
      state.burst.isLive = true
      state.burst.lastUpdate = Date.now()
      saveStateToLocalStorage(state)
    },
    clearBurstLive: state => {
      state.burst.isLive = false
      state.burst.lastUpdate = null
      saveStateToLocalStorage(state)
    },
    setBurstNoData: state => {
      // When receiving NO_DATA, mark as not live but keep timestamp
      state.burst.isLive = false
      saveStateToLocalStorage(state)
    },
  },
})

export const { setFluxLive, clearFluxLive, setFluxNoData,
  setBurstLive, clearBurstLive, setBurstNoData } = liveSlice.actions

export const selectFluxIsLive = (state: RootState) => state.live.flux.isLive

export const selectBurstIsLive = (state: RootState) => state.live.burst.isLive

export default liveSlice.reducer

